'use client';

// @ts-ignore
import googleAnalytics from '@analytics/google-analytics';
// @ts-ignore
import googleTagManager from '@analytics/google-tag-manager';
// @ts-ignore
import mixpanelPlugin from '@analytics/mixpanel';
import {
  EDGETAG_EDGE_URL,
  ENV,
  GOOGLE_ANALYTICS_MEASUREMENT_ID,
  GTM_CONTAINER_ID,
  MIXPANEL_TOKEN,
  SHOPIFY_CUSTOM_DOMAIN,
  TRIPLE_WHALE_ENABLED
} from '@petplate/settings';
import Analytics, { AnalyticsPlugin } from 'analytics';
import { usePathname } from 'next/navigation';
import React, { useEffect } from 'react';
import { edgetagPlugin } from './edgetagPlugin';
import {
  setTriplePixelAddToCart,
  setTriplePixelContact as setTriplePixelContactRaw,
  setTriplePixelProductView,
  triplePixelPlugin
} from './triplePixelPlugin';
import {
  createEdgetagHelpers,
  setEdgetagUser as setEdgetagUserRaw,
  getEdgetagUserId as getEdgetagUserIdRaw,
  appendEdgetagUserId as appendEdgetagUserIdRaw
} from './edgetagHelpers';

// NOTE: Mixpanel plugin only added when MIXPANEL_TOKEN is set (production only) — satisfies PET-27 BR2
const plugins: AnalyticsPlugin[] = [
  googleTagManager({
    containerId: GTM_CONTAINER_ID
  }),
  googleAnalytics({
    measurementIds: [GOOGLE_ANALYTICS_MEASUREMENT_ID]
  })
];

if (MIXPANEL_TOKEN) {
  const isProduction = ENV === 'production';
  plugins.push(
    mixpanelPlugin({
      token: MIXPANEL_TOKEN,
      options: {
        // NOTE: Session Replay — only enabled in production (BR4)
        record_sessions_percent: isProduction ? 100 : 0,
        record_mask_all_text: false,
        record_mask_all_inputs: false,
        record_block_selector: 'img, video',
        record_idle_timeout_ms: 1800000
      }
    })
  );
}

// AIDEV-NOTE: Edgetag (Blotout) plugin only added when EDGETAG_EDGE_URL is set.
// Same conditional pattern as Mixpanel — env-driven, no hard dep at build time.
// disableConsentCheck=true today; flip to false once the consent layer ships (Phase F).
if (EDGETAG_EDGE_URL) {
  plugins.push(
    edgetagPlugin({
      edgeURL: EDGETAG_EDGE_URL,
      disableConsentCheck: true
    })
  );
}

if (TRIPLE_WHALE_ENABLED) {
  plugins.push(triplePixelPlugin({ shop: SHOPIFY_CUSTOM_DOMAIN }));
}

export const analytics = Analytics({
  app: 'petplate',
  debug: true,
  plugins
});

// NOTE: returns true when an admin is impersonating a user via /sudo — suppresses analytics
const isSudoSession = () =>
  typeof window !== 'undefined' && sessionStorage.getItem('pp_sudo') === '1';

// AIDEV-NOTE: Edgetag-shaped helpers bound to apps/web's analytics instance. Plugin-scoped so they
// fire ONLY into the edgetag plugin (not GA4/GTM/Mixpanel). Subflow has its own analytics instance
// and creates its own bound copy of these helpers — see apps/subflow/.../AnalyticsProvider.tsx.
const edgetagHelpers = createEdgetagHelpers(analytics);
export const trackViewContent = (payload: Parameters<typeof edgetagHelpers.trackViewContent>[0]) => {
  if (isSudoSession()) return;
  edgetagHelpers.trackViewContent(payload);
};
export const trackAddToCart = (payload: Parameters<typeof edgetagHelpers.trackAddToCart>[0]) => {
  if (isSudoSession()) return;
  edgetagHelpers.trackAddToCart(payload);
};
export const trackInitiateCheckout = (
  payload: Parameters<typeof edgetagHelpers.trackInitiateCheckout>[0]
) => {
  if (isSudoSession()) return;
  edgetagHelpers.trackInitiateCheckout(payload);
};
export const trackLead = (payload: Parameters<typeof edgetagHelpers.trackLead>[0]) => {
  if (isSudoSession()) return;
  edgetagHelpers.trackLead(payload);
};
export const identifyContact = ({ email }: { email?: string }) => {
  if (isSudoSession()) return;
  setEdgetagUserRaw({ email });
  setTriplePixelContactRaw({ email });
};
export const trackTripleAddToCart = (items: Parameters<typeof setTriplePixelAddToCart>[0]) => {
  if (isSudoSession()) return;
  setTriplePixelAddToCart(items);
};
export const trackTripleProductView = (
  product: Parameters<typeof setTriplePixelProductView>[0]
) => {
  if (isSudoSession()) return;
  setTriplePixelProductView(product);
};
export const getEdgetagUserId = getEdgetagUserIdRaw;
export const appendEdgetagUserId = appendEdgetagUserIdRaw;

// AIDEV-NOTE: trackViewProductEvent fires GA4-shape `viewProduct` (legacy contract) AND Edgetag-shape
// `view_content` so PDPs (PdpHero) fire the Edgetag ViewContent tag without changing call sites.
// Edgetag track is plugin-scoped so the GA4/GTM/Mixpanel pipeline is unchanged.
export const trackViewProductEvent = ({
  itemId,
  itemName,
  price
}: {
  itemId: string;
  itemName: string;
  price: number;
}) => {
  if (isSudoSession()) return;
  analytics.track('viewProduct', {
    ecommerce: {
      currency: 'USD',
      items: [
        {
          item_id: itemId,
          item_name: itemName,
          price,
          quantity: 1
        }
      ]
    }
  });
  edgetagHelpers.trackViewContent({
    currency: 'USD',
    value: price,
    contents: [
      {
        id: itemId,
        quantity: 1,
        item_price: price,
        title: itemName
      }
    ]
  });
};

// NOTE: call this when user identity becomes available (login, sign-up, account pages)
// Uses Mixpanel special properties ($email, $name) so they map to People profile fields
export const identifyUser = (userId: string, traits?: Record<string, unknown>) => {
  if (isSudoSession()) return;
  if (!userId) return;
  const mappedTraits: Record<string, unknown> = { ...traits };
  if (traits?.email && traits.email !== 'undefined') {
    mappedTraits.$email = traits.email;
  }
  if (traits?.name && traits.name !== 'undefined') {
    mappedTraits.$name = traits.name;
  }
  analytics.identify(userId, mappedTraits);
};

export const useTrackPageChange = () => {
  const pathname = usePathname();

  useEffect(() => {
    if (isSudoSession()) return;
    analytics.page({
      event: 'pageview'
    });
  }, [pathname]);
};

const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useTrackPageChange();
  return <>{children}</>;
};

export default AnalyticsProvider;
